import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { DEPLOY_PRODUCTION } from '@/const'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import {
  useCoinPriceAPIStore,
  useCoinAPIStore,
  useInfoAPIStore,
} from '@/stores'
import type { Maybe } from '@/types'
import { Account } from '@/types/account/Account'
import type {
  IAccount,
  IWallet,
  ICreateAccountRequest,
  IUpdateAccountRequest,
  IGetAccountListRequest,
  IGetAccountRequest,
  IUpdateAccountListRequest,
} from '@/types/api'
import { getExecuteOnce, arrayPushUnique } from '@/utils'

const useAccountAPIStore = defineStore('accountAPI', () => {
  const coinPriceAPIStore = useCoinPriceAPIStore()
  const coinAPIStore = useCoinAPIStore()
  const infoAPIStore = useInfoAPIStore()
  const accountListByWalletKey = ref<Record<IWallet['id'], Maybe<IAccount[]>>>(
    {},
  )
  const accountList = computed<Maybe<IAccount[]>>(() => {
    const list: IAccount[] = []
    const seenIds = new Set<string>()
    const seenAddressLabel = new Set<string>() // 주소+라벨 중복 체크용 (마이그레이션 데이터 중복 대응)

    for (const [walletId, accounts] of Object.entries(
      accountListByWalletKey.value,
    )) {
      if (accounts && Array.isArray(accounts)) {
        accounts.forEach((account) => {
          if (seenIds.has(account.id)) {
            return
          }

          // 같은 wallet 내에서 같은 주소 + 같은 label을 가진 계정은 첫 번째만 사용
          const address =
            account.addresses?.[0] || (account as any).address || ''
          const addressLabelKey = `${walletId}-${address}-${account.label}`
          if (seenAddressLabel.has(addressLabelKey)) {
            return
          }

          seenIds.add(account.id)
          seenAddressLabel.add(addressLabelKey)
          list.push(account)
        })
      }
    }

    // DEPLOY_PRODUCTION이면 테스트넷 제거
    if (import.meta.env.VITE_DEPLOY_ENV === DEPLOY_PRODUCTION) {
      const filterNetworks = coinAPIStore.filterCoinList?.map((res) =>
        res.network?.toLowerCase(),
      )
      return list?.filter((account) => {
        const accountNetwork = account.currencyId?.toLowerCase()
        return !filterNetworks?.some(
          (network) => network && accountNetwork?.includes(network),
        )
      })
    }

    return list
  })

  const createAccount = async (params: ICreateAccountRequest) => {
    await fetchAccountList({ walletId: params.walletId })
    const res =
      await WepinBackendAPI.getInstance().account.createAccount(params)
    if (!accountListByWalletKey.value[params.walletId]) {
      accountListByWalletKey.value[params.walletId] = []
    }

    // 계정 생성 후 currency 정보 포함하여 다시 조회
    const accountWithCurrency =
      await WepinBackendAPI.getInstance().account.getAccount({
        accountId: res.account.id,
        walletId: params.walletId,
      })

    const fullAccount = accountWithCurrency?.account || res.account
    const accountInstance = new Account({ account: fullAccount as IAccount })
    accountInstance.fetchBalance(true)

    if (accountInstance.coin?.coinGeckoId) {
      coinPriceAPIStore.fetchCoinPrice({
        coinGeckoIds: [accountInstance.coin.coinGeckoId],
        currency: infoAPIStore.currentCurrency,
      })
    }

    accountListByWalletKey.value[params.walletId]?.push(fullAccount)
    return { ...res, account: fullAccount }
  }

  const updateAccount = async (
    accountId: IAccount['id'],
    params: IUpdateAccountRequest,
  ) => {
    if (!accountId || !params) return
    const res = await WepinBackendAPI.getInstance().account.updateAccount(
      { accountId },
      params,
    )
    if (res && accountList) {
      for (const [walletId, accountList] of Object.entries(
        accountListByWalletKey.value,
      )) {
        if (!accountList) continue
        const finded = accountList.find((account) => account.id === accountId)
        if (finded) {
          accountListByWalletKey.value[walletId] = accountListByWalletKey.value[
            walletId
          ]?.map((account) =>
            account.id === accountId ? res.account : account,
          )
        }
      }
    }
    return res
  }

  const deleteAccount = async (
    accountId: IAccount['id'],
    skipRefresh = false,
  ) => {
    if (!accountId) return

    // accountId로부터 walletId 찾기
    let foundWalletId = ''
    for (const [walletId, accountList] of Object.entries(
      accountListByWalletKey.value,
    )) {
      if (accountList?.find((account) => account.id === accountId)) {
        foundWalletId = walletId
        break
      }
    }

    const res = await WepinBackendAPI.getInstance().account.deleteAccount({
      accountId,
      walletId: foundWalletId || localStorage.getItem('defaultWalletId') || '',
    })

    if (res.result && !skipRefresh) {
      // Wallet 데이터 새로고침 (account 목록 업데이트)
      const { useWalletAPIStore } = await import('@/stores')
      const walletAPIStore = useWalletAPIStore()
      await walletAPIStore.fetchWalletList({}, true)
    }
    return res
  }

  const MAX_ACCOUNT_LIST = 20
  const DEFAULT_PAGE = 1
  const fetchAccountList = getExecuteOnce(
    async (
      params: IGetAccountListRequest,
      force: boolean | undefined = false,
      page = DEFAULT_PAGE,
      limit = MAX_ACCOUNT_LIST,
    ) => {
      // fetchWalletList에서 이미 accounts를 캐싱했으므로 캐시가 있으면 사용
      const cached = accountListByWalletKey.value[params?.walletId!]
      if (cached !== undefined) {
        return cached
      }

      // 캐시가 없을 때만 API 호출
      if (page) params.page = page
      params.limit = limit
      const res = await WepinBackendAPI.getInstance().account.getAccountList(
        params!,
      )
      if (!res) return
      accountListByWalletKey.value[params?.walletId!] = arrayPushUnique(
        res.accounts,
        'id',
        accountListByWalletKey.value[params?.walletId!] as IAccount[],
      )
      if (res.accounts.length >= MAX_ACCOUNT_LIST) {
        await fetchAccountList(params, force, page + 1, limit)
      }
      return accountListByWalletKey.value[params?.walletId!]
    },
  )

  const updateAccountList = getExecuteOnce(
    async (params?: IUpdateAccountListRequest) => {
      const res = await WepinBackendAPI.getInstance().account.updateAccountList(
        params!,
      )
      if (!res) return
      accountListByWalletKey.value[params?.walletId!] = res.accounts
      return accountListByWalletKey.value[params?.walletId!]
    },
  )

  const getAccount = getExecuteOnce(
    async (params: IGetAccountRequest, walletId: string) => {
      const res = await WepinBackendAPI.getInstance().account.getAccount({
        ...params,
        walletId,
      })
      if (!res) return
      const index = accountListByWalletKey.value[walletId]?.findIndex(
        (account) => account.id === params.accountId,
      )
      if (index !== undefined && index !== -1) {
        accountListByWalletKey.value[walletId]![index] = res.account
      }

      // V2: balance도 accountBalanceList에 업데이트
      if (res.account?.balance !== undefined) {
        const { useAccountBalanceAPIStore } = await import('@/stores')
        const accountBalanceStore = useAccountBalanceAPIStore()
        accountBalanceStore.accountBalanceList[params.accountId] =
          res.account.balance
      }

      return res.account
    },
  )

  const reset = () => {
    accountListByWalletKey.value = {}
  }

  return {
    accountList,
    accountListByWalletKey,
    createAccount,
    updateAccount,
    deleteAccount,
    fetchAccountList,
    updateAccountList,
    getAccount,
    reset,
  }
})

export { useAccountAPIStore }
